// Constants
import { SERVICE_DOMAINS } from '../constants/services';

// Types
export type IOption = {
  optionId: string;
  sorting: number;
  value: string;
  recipientCount: number;
};

// Utilities
import { request } from '../utilities/http';

// Services
export interface IGetQuestionRequest {
  params: { networkId: string };
  query: {
    questionId: string;
  };
}

export interface IGetQuestionResponse {
  activeFrom: string;
  activeTo: string;
  authorFirstName: string;
  authorLastName: string;
  authorUserId: string;
  authorPhotoUrl: string;
  created: string;
  description: string;
  networkId: string;
  options: IOption[];
  questionId: string;
  recipientCount: number;
  networkName: string;
  commentCount: number;
}

export function getQuestion({
  params,
  query
}: IGetQuestionRequest): Promise<IGetQuestionResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.get`,
    {
      method: 'GET',
      query
    }
  );
}

export interface IGetQuestionsRequest {
  params: { networkId: string };
}

export interface IGetQuestionsResponse {
  activeFrom: string;
  activeTo: string;
  authorFirstName: string;
  authorLastName: string;
  authorUserId: string;
  authorPhotoUrl: string;
  created: string;
  description: string;
  networkId: string;
  options: IOption[];
  questionId: string;
  recipientCount: number;
  networkName: string;
  commentCount: number;
}

export function getQuestions({
  params
}: IGetQuestionsRequest): Promise<IGetQuestionsResponse[]> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.list`,
    {
      method: 'GET',
      query: {
        offset: 0,
        limit: 50
      }
    }
  );
}

export interface ICreateQuestionsRequest {
  params: { networkId: string };
  body: {
    description: string;
    activeFrom: string;
    activeTo: string;
  };
}

export interface ICreateQuestionsResponse {
  networkId: string;
  authorUserId: string;
  questionId: string;
  description: string;
  activeFrom: string;
  activeTo: string;
}

export function createQuestion({
  params,
  body
}: ICreateQuestionsRequest): Promise<ICreateQuestionsResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.create`,
    {
      method: 'POST',
      body: JSON.stringify(body)
    }
  );
}

export interface IDeleteQuestionsRequest {
  params: { networkId: string };
  query: {
    questionId: string;
  };
}

export interface IDeleteQuestionsResponse {}

export function deleteQuestion({
  params,
  query
}: IDeleteQuestionsRequest): Promise<IDeleteQuestionsResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.delete`,
    {
      method: 'POST',
      query
    }
  );
}

export interface ICreateQuestionsOptionsRequest {
  params: { networkId: string };
  body: Array<{
    networkId: string; // TODO: Temporary fix for API issues
    questionId: string;
    value: string;
    sorting: number;
  }>;
}

export interface ICreateQuestionsOptionsResponse {}

export function createQuestionOptions({
  params,
  body
}: ICreateQuestionsOptionsRequest): Promise<ICreateQuestionsOptionsResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.options.create`,
    {
      method: 'POST',
      body: JSON.stringify(body)
    }
  );
}

export interface IGetQuestionsRecipientsRequest {
  params: { networkId: string };
  query: { questionId: string };
}

export interface IGetQuestionsRecipientsResponse {
  networkId: string;
  questionId: string;
  userId: string;
  optionId: string;
  answered: string;
  value: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
}

export function getQuestionRecipients({
  params,
  query
}: IGetQuestionsRecipientsRequest): Promise<IGetQuestionsRecipientsResponse[]> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.recipients.list`,
    {
      method: 'GET',
      query
    }
  );
}

export interface ICreateQuestionsRecipientsRequest {
  params: { networkId: string };
  body: Array<{
    networkId: string; // TODO: Temporary fix for API issues
    questionId: string;
    userId: string;
  }>;
}

export interface ICreateQuestionsRecipientsResponse {}

export function createQuestionRecipients({
  params,
  body
}: ICreateQuestionsRecipientsRequest): Promise<
  ICreateQuestionsRecipientsResponse
> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.recipients.create`,
    {
      method: 'POST',
      body: JSON.stringify(body)
    }
  );
}

export interface ICreateQuestionsAnswerRequest {
  params: { networkId: string };
  query: {
    questionId: string;
    optionId: string;
  };
}

export interface ICreateQuestionsAnswerResponse {}

export function createQuestionAnswer({
  params,
  query
}: ICreateQuestionsAnswerRequest): Promise<ICreateQuestionsAnswerResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.answers.create`,
    {
      method: 'POST',
      query
    }
  );
}

export interface IGetQuestionsCommentRequest {
  params: { networkId: string };
  query: { questionId: string };
}

export interface IGetQuestionsCommentResponse {
  networkId: string;
  questionId: string;
  commentId: string;
  userId: string;
  created: string;
  comment: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
}

export function getQuestionComments({
  params,
  query
}: IGetQuestionsCommentRequest): Promise<IGetQuestionsCommentResponse[]> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.comments.list`,
    {
      method: 'GET',
      query
    }
  );
}

export interface ICreateQuestionsCommentRequest {
  params: { networkId: string };
  query: { questionId: string };
  body: {
    comment: string;
  };
}

export interface ICreateQuestionsCommentResponse {
  networkId: string;
  questionId: string;
  commentId: string;
  userId: string;
  created: string;
  comment: string;
  firstName: string;
  lastName: string;
  photoUrl: string;
}

export function createQuestionComments({
  params,
  query,
  body
}: ICreateQuestionsCommentRequest): Promise<ICreateQuestionsCommentResponse> {
  return request(
    `${SERVICE_DOMAINS.API}/v1/${params.networkId}/questions.comments.create`,
    {
      method: 'POST',
      query,
      body: JSON.stringify(body)
    }
  );
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/services/question.ts